import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Upload } from "lucide-react";
import type { UseFormReturn } from "react-hook-form";
import { Field } from "../../Form";
import Button from "../../Button";
import ResourceSelect from "../ResourceSelect";
import ImportDialog from "./ImportDialog";
import { cn } from "../../../../lib/utils";
import type { ResourceItem, ResourceSelectProps } from "../ResourceSelect/types";

type ResourceSelectWithImportProps<T extends { id: string | number } = ResourceItem> =
    Omit<ResourceSelectProps<T>, "value" | "onChange"> & {
        form: UseFormReturn<any>;
        name: string;
        label?: string;
        className?: string;
        fieldClassName?: string;
    };

function ResourceSelectWithImport<T extends { id: string | number } = ResourceItem>({
    form,
    name,
    label,
    className,
    fieldClassName,
    ...props
}: ResourceSelectWithImportProps<T>) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);

    return (
        <>
            <div className={cn("tw-flex tw-items-end tw-gap-2", className)}>
                <Field name={name} label={label} className={cn("tw-mb-0 tw-flex-1", fieldClassName)}>
                    <ResourceSelect allowClear {...(props as ResourceSelectProps<T>)} />
                </Field>

                {props.mode === "multiple" && (
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        tooltip={t("import")}
                        onClick={() => setOpen(true)}
                    >
                        <Upload className="tw-size-4 tw-text-primary" />
                    </Button>
                )}
            </div>

            <ImportDialog
                form={form}
                name={name}
                resourcePath={props.resourcePath}
                getSearchFilter={props.getSearchFilter}
                open={open}
                close={() => setOpen(false)}
            />
        </>
    );
}

export default ResourceSelectWithImport;
